import { ENDPOINTS } from 'config/endpoints';
import { normalizeFormAggregationRow } from 'entities/FormAggregationRow';
import { normalizeTableRowEntity } from 'entities/TableRow';
import {
  FormPollUpdateRequestType,
  FormPollUpdateResponseType,
} from 'entities/endpoints/form.poll_update';
import { FormTableDataUploadRequest } from 'entities/endpoints/form.table_data.upload';
import { ApiRequest, PollApiRequest } from 'stores/common/ApiRequest';
import { ILocalStore } from 'stores/interfaces';

import { ITableFactoryStore } from '../../TableFactoryStore';
import { CellUpdateParams } from '../types';

const UPDATE_POLL_INTERVAL = 5000;

type TableFactoryRowUpdateManagerParams = {
  tableFactoryStore: ITableFactoryStore;
};

/** Стор для обработки обновления ячеек */
class TableFactoryRowUpdateManager implements ILocalStore {
  constructor({ tableFactoryStore }: TableFactoryRowUpdateManagerParams) {
    this._pollUpdateService = new PollApiRequest<
      FormPollUpdateRequestType,
      FormPollUpdateResponseType
    >({
      callback: (params: FormPollUpdateResponseType) =>
        this._handleCellUpdate(params),
      endpoint: ENDPOINTS.form.pollUpdate,
      methodType: 'GET',
      interval: UPDATE_POLL_INTERVAL,
    });

    this._tableFactoryStore = tableFactoryStore;
  }

  private readonly _tableFactoryStore: ITableFactoryStore;

  /** Последнее время обновления таблицы ( для запроса в поллинге ) */
  private _lastUpdateTime: string;

  readonly updateCellRequest = new ApiRequest({
    methodType: 'POST',
    endpoint: ENDPOINTS.form.tableDataUpdate,
  });

  private readonly _pollUpdateService: PollApiRequest<
    FormPollUpdateRequestType,
    FormPollUpdateResponseType
  >;

  private _handleCellUpdate(response: FormPollUpdateResponseType): void {
    this._lastUpdateTime = response.last_update_dt;

    /** Проверяем, были ли обновления в карусели агрегаций и обновляем данные, если пришли новые */
    if (response.aggregation_row) {
      const rowsToUpdate = response.aggregation_row.map((row) =>
        normalizeFormAggregationRow(row)
      );

      this._tableFactoryStore.tableAggregationStore.updateAggregationRow(
        rowsToUpdate
      );
    }

    /** Проверяем, были ли обновления в строке агрегаций и обновляем данные, если пришли новые */
    if (response.footer_row) {
      this._tableFactoryStore.tableAggregationStore.updateAggregationFooterRow(
        normalizeTableRowEntity(response.footer_row)
      );
    }

    /** Проверяем, были ли обновления в самой таблице и обновляем данные, если пришли новые */
    if (response.updated_rows.length > 0) {
      response.updated_rows.forEach((updatedRow) => {
        const entity = normalizeTableRowEntity(updatedRow);

        // Update the data store (for virtualized mode)
        this._tableFactoryStore.rowDataStore.updateRow(entity.rowId, entity);

        // Update cached row model if it exists (virtualized mode)
        const cachedModel = this._tableFactoryStore.rowModelCache.get(
          entity.rowId
        );

        if (cachedModel) {
          cachedModel.updateEntity(entity);
        }

        // Legacy mode: update via subRowsMap
        const rowToUpdate = this._tableFactoryStore.subRowsMap[updatedRow.row_id];

        if (rowToUpdate && rowToUpdate !== cachedModel) {
          rowToUpdate.updateEntity(entity);
        }
      });
    }
  }

  async updateCell(params: CellUpdateParams): Promise<void> {
    if (!this._tableFactoryStore.callParams) {
      return;
    }

    const { isError } = await this.updateCellRequest.call<
      null,
      FormTableDataUploadRequest
    >({
      data: {
        id: this._tableFactoryStore.callParams.form_id,
        rows: [
          {
            id: params.rowId,
            columns: [
              {
                id: params.cellId,
                new_value: params.cellValue,
              },
            ],
          },
        ],
      },
    });

    if (isError) {
      return;
    }
  }

  async start(): Promise<void> {
    if (
      !this._tableFactoryStore.callParams ||
      !this._tableFactoryStore.callParams.root_record_id
    ) {
      return;
    }

    await this._pollUpdateService.start({
      data: {
        view: this._tableFactoryStore.callParams?.view,
        form_id: this._tableFactoryStore.callParams?.form_id,
        last_update_dt:
          this._lastUpdateTime || this._tableFactoryStore.lastUpdateDate,
        root_record_id: this._tableFactoryStore.callParams?.root_record_id,
        opened_record_ids: this._tableFactoryStore.expandedRowsKeys.join(','),
      },
    });
  }

  destroy() {
    this._pollUpdateService.destroy();
    this.updateCellRequest.destroy();
  }
}

export default TableFactoryRowUpdateManager;
